import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { eur } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, X, History, Handshake, CalendarDays, Mail, Send, Eraser, RotateCcw, StickyNote, ChevronsUpDown, Check, Search } from "lucide-react";
import { toast } from "sonner";

const chipClass = (pct) => pct == null ? "bg-muted text-muted-foreground"
  : pct >= 100 ? "bg-emerald-500/15 text-emerald-400"
  : pct > 70 ? "bg-amber-500/15 text-amber-400"
  : "bg-red-500/15 text-red-400";

// Selettore anagrafica con ricerca: renderizza al massimo 50 risultati filtrati
// (evita di montare centinaia di opzioni insieme, che manda in crash Safari su iPhone).
function AnagraficaPicker({ value, onChange, disabled, anagrafiche = [], testid, barrato }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = anagrafiche.find((a) => a.id === value);
  const ql = q.trim().toLowerCase();
  const filtered = ql ? anagrafiche.filter((a) => (a.nome || "").toLowerCase().includes(ql)) : anagrafiche;
  const shown = filtered.slice(0, 50);
  const more = filtered.length - shown.length;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled}
          className={`h-9 min-w-0 flex-1 justify-between font-normal ${!selected ? "text-muted-foreground" : ""} ${barrato ? "line-through" : ""}`}
          data-testid={testid}>
          <span className="truncate">{selected ? selected.nome : "Seleziona anagrafica…"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca anagrafica…"
            className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            data-testid={testid ? `${testid}-search` : undefined} />
        </div>
        <div className="max-h-64 overflow-y-auto p-1" data-testid={testid ? `${testid}-list` : undefined}>
          {shown.length === 0 ? (
            <div className="px-2 py-3 text-center text-sm text-muted-foreground">Nessuna anagrafica.</div>
          ) : shown.map((a) => (
            <button key={a.id} type="button"
              onClick={() => { onChange(a.id); setOpen(false); setQ(""); }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent"
              data-testid={testid ? `${testid}-opt-${a.id}` : undefined}>
              <Check className={`h-4 w-4 shrink-0 ${a.id === value ? "opacity-100" : "opacity-0"}`} />
              <span className="truncate">{a.nome} <span className="text-muted-foreground">· {a.tipo}</span></span>
            </button>
          ))}
          {more > 0 && <div className="px-2 py-1.5 text-center text-[11px] text-muted-foreground">+{more} altri — affina la ricerca</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CommitCard({ agencyId, tipo, canEdit, anagrafiche, icon: Icon, title }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [nota, setNota] = useState("");
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const { user } = useAuth();
  const canSend = ["admin", "back_office", "commercial_manager", "head_of_partner"].includes(user?.role);
  const isMgmt = ["admin", "back_office", "commercial_manager", "head_of_partner"].includes(user?.role);
  const [ordini, setOrdini] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [rep, setRep] = useState({ week: false, month: false });
  const [sender, setSender] = useState("back_office");
  const [ccRoles, setCcRoles] = useState({ back_office: false, direzione: false, responsabile: false });
  const [ccFree, setCcFree] = useState("");

  const openSend = () => {
    setRep({ week: tipo === "week", month: tipo === "month" });
    setSender("back_office");
    setCcRoles({ back_office: false, direzione: false, responsabile: false });
    setCcFree("");
    setSendOpen(true);
  };
  const doSend = async () => {
    const reports = Object.keys(rep).filter((k) => rep[k]);
    if (!reports.length) { toast.error("Seleziona almeno un report"); return; }
    setSending(true);
    try {
      const cc_roles = Object.keys(ccRoles).filter((k) => ccRoles[k]);
      const cc_emails = ccFree.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
      const r = await api.post("/agency-commitments/send-email", { agency_id: agencyId, reports, sender, cc_roles, cc_emails });
      toast.success(`Email inviata a ${r.data.inviate} destinatario/i${r.data.cc?.length ? ` · CC: ${r.data.cc.length}` : ""}`);
      setSendOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); } finally { setSending(false); }
  };

  const load = () => {
    api.get(`/agency-commitments?tipo=${tipo}&agency_id=${agencyId}`)
      .then((r) => { setData(r.data); setRows(r.data.righe || []); setNota(r.data.nota || ""); setDirty(false); })
      .catch(() => setData(false));
    api.get(`/agency-commitments/ordini?tipo=${tipo}&agency_id=${agencyId}`)
      .then((r) => setOrdini(r.data)).catch(() => setOrdini({ ordini: [], totale_eur: 0 }));
  };
  useEffect(() => { if (agencyId) load(); /* eslint-disable-next-line */ }, [agencyId, tipo]);

  const addRow = () => { setRows((s) => [...s, { ref_id: "", ref_tipo: "", cliente: "", importo_eur: "" }]); setDirty(true); };
  const removeRow = (i) => { setRows((s) => s.filter((_, k) => k !== i)); setDirty(true); };
  const setRef = (i, refId) => {
    const a = anagrafiche.find((x) => x.id === refId);
    setRows((s) => s.map((r, k) => k === i ? { ...r, ref_id: refId, ref_tipo: a?.tipo || "", cliente: a?.nome || "" } : r));
    setDirty(true);
  };
  const setImporto = (i, v) => { setRows((s) => s.map((r, k) => k === i ? { ...r, importo_eur: v } : r)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const clean = rows.filter((r) => r.ref_id && Number(r.importo_eur) > 0)
        .map((r) => ({ rid: r.rid, ref_id: r.ref_id, ref_tipo: r.ref_tipo, cliente: r.cliente, importo_eur: Number(r.importo_eur) }));
      await api.put("/agency-commitments", { agency_id: agencyId, tipo, periodo: data?.periodo, righe: clean, nota });
      toast.success("Impegno salvato");
      load();
    } catch { toast.error("Salvataggio non riuscito"); } finally { setSaving(false); }
  };

  const bar = async (rid) => {
    try {
      await api.post("/agency-commitments/bar", { agency_id: agencyId, tipo, periodo: data?.periodo, rid, barrato: true });
      load();
    } catch { toast.error("Operazione non riuscita"); }
  };
  const restore = async (rid) => {
    try {
      await api.post("/agency-commitments/bar", { agency_id: agencyId, tipo, periodo: data?.periodo, rid, barrato: false });
      load();
    } catch { toast.error("Ripristino non riuscito"); }
  };

  const openHist = () => {
    setHistOpen(true); setHist(null);
    api.get(`/agency-commitments/storico?tipo=${tipo}&agency_id=${agencyId}`).then((r) => setHist(r.data.storico || [])).catch(() => setHist([]));
  };

  const totale = rows.reduce((s, r) => s + (r.barrato ? 0 : (Number(r.importo_eur) || 0)), 0);
  const totaleLordo = rows.reduce((s, r) => s + (Number(r.importo_eur) || 0), 0);
  const hasBarred = rows.some((r) => r.barrato);
  const obj = data?.obiettivo_eur || 0;
  const pct = obj > 0 ? Math.round(totale / obj * 100) : null;

  return (
    <Card className="border-amber-400/30 bg-amber-400/[0.06] p-4 shadow-sm" data-testid={`impegni-card-${tipo}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <div>
            <h3 className="font-heading text-sm font-black leading-tight">{title}</h3>
            <p className="text-[11px] text-muted-foreground">{data?.label || "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-500" data-testid={`impegni-postit-label-${tipo}`}><StickyNote className="h-3.5 w-3.5" /> Post-it</span>
          {canSend && (
            <button type="button" onClick={openSend} title="Invia impegni via email all'agente" className="text-muted-foreground transition-colors hover:text-primary" data-testid={`impegni-mail-btn-${tipo}`}>
              <Mail className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={openHist} title={tipo === "week" ? "Settimane precedenti" : "Mesi precedenti"} className="text-muted-foreground transition-colors hover:text-primary" data-testid={`impegni-storico-btn-${tipo}`}>
            <History className="h-4 w-4" />
          </button>
        </div>
      </div>

      {data === null ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico…</div>
      ) : (
        <>
          <div className="space-y-2">
            {rows.length === 0 && <p className="text-xs text-muted-foreground">Nessun impegno inserito per questo periodo.</p>}
            {rows.map((r, i) => {
              const editable = canEdit && !r.barrato && (isMgmt || !r.rid);
              return (
              <div key={r.rid || i} className={`flex items-center gap-2 ${r.barrato ? "opacity-60" : ""}`} data-testid={`impegni-row-${tipo}-${i}`}>
                <AnagraficaPicker value={r.ref_id} onChange={(v) => setRef(i, v)} disabled={!editable} anagrafiche={anagrafiche} barrato={r.barrato} testid={`impegni-select-${tipo}-${i}`} />
                <Input type="number" min="0" step="100" value={r.importo_eur} onChange={(e) => setImporto(i, e.target.value)} placeholder="€ ordine" className={`h-9 w-28 shrink-0 ${r.barrato ? "line-through" : ""}`} disabled={!editable} data-testid={`impegni-importo-${tipo}-${i}`} />
                {r.riportato && !r.barrato && (() => { const n = r.riportato_count || 1; const hot = n >= 3;
                  return <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${hot ? "bg-red-500/20 text-red-500" : "bg-amber-500/20 text-amber-500"}`} title={`Impegno rimandato ${n} ${n === 1 ? "volta" : "volte"} dai periodi precedenti`} data-testid={`impegni-riportato-${tipo}-${i}`}>Riportato{n > 1 ? ` ×${n}` : ""}</span>; })()}
                {r.barrato ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <span className="rounded bg-zinc-500/20 px-1.5 py-0.5 text-[10px] font-bold text-zinc-400" data-testid={`impegni-barrato-badge-${tipo}-${i}`}>Barrato</span>
                    {canEdit && <button type="button" onClick={() => restore(r.rid)} title="Ripristina" className="text-muted-foreground hover:text-primary" data-testid={`impegni-restore-${tipo}-${i}`}><RotateCcw className="h-4 w-4" /></button>}
                    {isMgmt && canEdit && <button type="button" onClick={() => removeRow(i)} title="Elimina definitivamente" className="text-muted-foreground hover:text-red-500" data-testid={`impegni-remove-${tipo}-${i}`}><X className="h-4 w-4" /></button>}
                  </div>
                ) : (canEdit && (
                  <div className="flex shrink-0 items-center gap-1">
                    {r.rid && <button type="button" onClick={() => bar(r.rid)} title="Barra impegno" className="text-muted-foreground hover:text-primary" data-testid={`impegni-bar-${tipo}-${i}`}><Eraser className="h-4 w-4" /></button>}
                    {(isMgmt || !r.rid) && <button type="button" onClick={() => removeRow(i)} title={r.rid ? "Elimina" : "Rimuovi riga"} className="text-muted-foreground hover:text-red-500" data-testid={`impegni-remove-${tipo}-${i}`}><X className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
              );
            })}
          </div>

          {(isMgmt || (nota && nota.trim())) && (
            <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-400/[0.10] p-2.5" data-testid={`impegni-nota-${tipo}`}>
              <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-500"><StickyNote className="h-3.5 w-3.5" /> Nota condivisa (Direzione → Agenzia)</div>
              {isMgmt && canEdit ? (
                <textarea value={nota} onChange={(e) => { setNota(e.target.value); setDirty(true); }} rows={2}
                  placeholder="Nota per l'agenzia… si mantiene nei periodi successivi e viene inviata via email."
                  className="w-full resize-y rounded bg-background/50 p-2 text-sm outline-none placeholder:text-muted-foreground"
                  data-testid={`impegni-nota-input-${tipo}`} />
              ) : (
                <p className="whitespace-pre-wrap text-sm text-foreground" data-testid={`impegni-nota-text-${tipo}`}>{nota}</p>
              )}
            </div>
          )}

          {canEdit && (
            <div className="mt-3 flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addRow} data-testid={`impegni-add-${tipo}`}><Plus className="mr-1 h-4 w-4" /> Aggiungi riga</Button>
              <Button size="sm" onClick={save} disabled={saving || !dirty} data-testid={`impegni-save-${tipo}`}>{saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Salva</Button>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/50 pt-3 text-sm">
            <span className="text-muted-foreground">Impegnato {hasBarred && <b className="mr-1 font-mono text-muted-foreground line-through">{eur(totaleLordo)}</b>}<b className="font-mono text-foreground">{eur(totale)}</b> / obiettivo <b className="font-mono text-foreground">{eur(obj)}</b></span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${chipClass(pct)}`} data-testid={`impegni-pct-${tipo}`}>{pct == null ? "obiettivo n/d" : `${pct}%`}</span>
          </div>
          <p className="mt-2 text-[10px] leading-tight text-muted-foreground">Solo anagrafiche già presenti (clienti, partner e prospect dell'agenzia). Se un nominativo non è a sistema, va prima inserito.</p>
          <div className="mt-3 rounded-md border border-border/60 bg-background/40 p-2" data-testid={`impegni-ordini-${tipo}`}>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Ordini arrivati · {tipo === "week" ? "settimana" : "mese"}</span>
              {ordini && <span className="font-mono text-xs font-bold text-emerald-500" data-testid={`impegni-ordini-tot-${tipo}`}>{eur(ordini.totale_eur)}</span>}
            </div>
            {!ordini ? (
              <p className="text-[11px] text-muted-foreground">Carico…</p>
            ) : (ordini.ordini || []).length === 0 ? (
              <p className="text-[11px] text-muted-foreground">Nessun ordine arrivato nel periodo.</p>
            ) : (
              <div className="space-y-0.5">
                {ordini.ordini.map((o, k) => (
                  <div key={k} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`impegni-ordine-${tipo}-${k}`}>
                    <span className="truncate"><span className="font-mono text-muted-foreground">{o.numero}</span> · {o.cliente}</span>
                    <span className="shrink-0 font-mono">{eur(o.importo)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-md" data-testid={`impegni-storico-${tipo}`}>
          <DialogHeader><DialogTitle>{tipo === "week" ? "Settimane precedenti" : "Mesi precedenti"}</DialogTitle></DialogHeader>
          {hist === null ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico…</div>
          ) : hist.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">Nessun periodo precedente registrato.</p>
          ) : (
            <div className="max-h-80 space-y-1.5 overflow-y-auto">
              {hist.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2 text-sm" data-testid={`impegni-storico-row-${tipo}-${i}`}>
                  <span>{h.label}</span>
                  <span className="font-mono text-muted-foreground">{eur(h.totale_eur)} <span className="text-[10px]">({h.n_righe} righe)</span></span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md" data-testid={`impegni-send-${tipo}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Invia impegni all'agente</DialogTitle>
            <DialogDescription>Destinatario: agenti dell'agenzia. Scegli mittente, copia e report da inviare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Report da inviare</Label>
              <div className="mt-1.5 flex gap-5">
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={rep.week} onCheckedChange={(v) => setRep((s) => ({ ...s, week: !!v }))} data-testid={`send-rep-week-${tipo}`} /> Settimanale</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={rep.month} onCheckedChange={(v) => setRep((s) => ({ ...s, month: !!v }))} data-testid={`send-rep-month-${tipo}`} /> Mensile</label>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Mittente (Da)</Label>
              <Select value={sender} onValueChange={setSender}>
                <SelectTrigger className="mt-1.5 h-9" data-testid={`send-sender-${tipo}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="back_office">Back Office</SelectItem>
                  <SelectItem value="direzione">Direzione</SelectItem>
                  <SelectItem value="responsabile">Responsabile Commerciale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">In copia (CC)</Label>
              <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.back_office} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, back_office: !!v }))} data-testid={`send-cc-bo-${tipo}`} /> Back Office</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.direzione} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, direzione: !!v }))} data-testid={`send-cc-dir-${tipo}`} /> Direzione</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.responsabile} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, responsabile: !!v }))} data-testid={`send-cc-resp-${tipo}`} /> Responsabile</label>
              </div>
              <Input value={ccFree} onChange={(e) => setCcFree(e.target.value)} placeholder="Email aggiuntive in copia (separate da virgola)" className="mt-2 h-9" data-testid={`send-cc-free-${tipo}`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Annulla</Button>
            <Button onClick={doSend} disabled={sending} data-testid={`send-submit-${tipo}`}>{sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Invia email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function ImpegniChiusura({ agencyId, canEdit = false }) {
  const [anagrafiche, setAnagrafiche] = useState(null);
  useEffect(() => {
    if (!agencyId) return;
    api.get(`/agency-commitments/anagrafiche?agency_id=${agencyId}`).then((r) => setAnagrafiche(r.data.anagrafiche || [])).catch(() => setAnagrafiche([]));
  }, [agencyId]);

  if (anagrafiche === null) return null;

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="impegni-chiusura">
      <CommitCard agencyId={agencyId} tipo="week" canEdit={canEdit} anagrafiche={anagrafiche} icon={Handshake} title="Impegni di chiusura · settimana" />
      <CommitCard agencyId={agencyId} tipo="month" canEdit={canEdit} anagrafiche={anagrafiche} icon={CalendarDays} title="Impegno mensile" />
    </div>
  );
}
